import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Chess } from 'chess.js';
// import useStockfishAnalysis from '../../hooks/useStockfishAnalysis'; // COMMENTED OUT - Using unified hook instead
import { useUnifiedStockfishAnalysis } from '../../hooks/useUnifiedStockfishAnalysis';
import StockfishAnalysisLines from './StockfishAnalysisLines';
import stockfish3Instance from '../../util/Stockfish3';
import { getPositionData, hasPositionAnalysis, getPositionAnalysis } from '../../util/PositionData';
import '../../styles/StockfishAnalysisPanel.css';

/**
 * StockfishAnalysisPanel - Displays Stockfish analysis and provides integration with 3Board
 */
const StockfishAnalysisPanel = ({
  fen,
  onMoveSelect,
  compact = false,
  showControls = true,
  analysisData = null,  // External analysis data from parent
  isAnalyzing: externalIsAnalyzing = false, // External analyzing state
  showStockfish = true,
  onAnalysisUpdate = null  // Callback to send analysis data to parent
}) => {
  // COMPREHENSIVE RE-RENDER TRACKING
  const renderCount = React.useRef(0);
  const prevPropsRef = React.useRef({});
  renderCount.current += 1;
  
  const currentProps = { fen, analysisData, externalIsAnalyzing, showStockfish };
  const prevProps = prevPropsRef.current;
  
  // console.log(`🔄 StockfishAnalysisPanel RENDER #${renderCount.current} [${Date.now()}]:`, {
  //   fen: fen?.substring(0, 20) + '...',
  //   hasAnalysisData: !!analysisData,
  //   analysisDataPvs: analysisData?.pvs?.length || 0,
  //   analysisDataTimestamp: analysisData?.timestamp || 'unknown',
  //   externalIsAnalyzing,
  //   showStockfish,
  //   compact,
  //   propsChanged: {
  //     fenChanged: fen !== prevProps.fen,
  //     analysisDataChanged: analysisData !== prevProps.analysisData,
  //     isAnalyzingChanged: externalIsAnalyzing !== prevProps.externalIsAnalyzing,
  //     showStockfishChanged: showStockfish !== prevProps.showStockfish
  //   }
  // });
  
  // Update refs for next render comparison
  prevPropsRef.current = currentProps;
  
  // Debug props on initialization
  

  const [expanded, setExpanded] = useState(!compact);
  const [displayMode, setDisplayMode] = useState('detailed'); // 'detailed' or 'simple'
  
  // New tab state management
  const [activeTab, setActiveTab] = useState(0); // 0=top3, 1=pv, 2=settings
  const [selectedPVIndex, setSelectedPVIndex] = useState(0); // for tab 2 navigation
  const [engineArrows, setEngineArrows] = useState('Hover'); // for tab 3
  const [depth, setDepth] = useState(15); // for tab 3
  const [hoveredMoveIndex, setHoveredMoveIndex] = useState(null); // for hover arrows
  
  // Track current move being played and target move for highlighting
  const [currentPlayingMoveIndex, setCurrentPlayingMoveIndex] = useState(null);
  const [targetMoveIndex, setTargetMoveIndex] = useState(null);
  
  // Use the unified Stockfish analysis hook to prevent re-renders
  const {
    analysis,
    isAnalyzing,
    source,
    error,
    forceReanalyze
  } = useUnifiedStockfishAnalysis(fen, {
    externalData: analysisData,
    forceLive: false,
    targetDepth: 15,
    onAnalysisUpdate: onAnalysisUpdate
  });
  
  // OLD ANALYSIS LOGIC - COMMENTED OUT (Now handled by useUnifiedStockfishAnalysis)
  /*
  // Direct access to window.currentAnalysisCache if available
  useEffect(() => {
    // Check if window.currentAnalysisCache exists and is valid
    if (window.currentAnalysisCache && 
        window.currentAnalysisCache.lines && 
        window.currentAnalysisCache.lines.length > 0) {
    //   
      setLocalAnalysis(window.currentAnalysisCache);
    }
    
    // Setup listener for Stockfish results
    const handleStockfishResult = (result) => {
    //   
      setLocalAnalysis(result);
    };
    
    // Replace or add the global callback
    const originalCallback = window.receiveStockfishAnalysis;
    window.receiveStockfishAnalysis = (result) => {
      // Call the original callback if it exists
      if (typeof originalCallback === 'function') {
        originalCallback(result);
      }
      
      // Also handle the result in this component
      handleStockfishResult(result);
    };
    
    // Clean up on unmount
    return () => {
      // Restore original callback if needed
      if (originalCallback) {
        window.receiveStockfishAnalysis = originalCallback;
      }
    };
  }, []);
  */
  
  // Analysis is now provided by useUnifiedStockfishAnalysis hook - no more local logic needed
  
  // Determine the final analyzing state from unified hook
  const finalIsAnalyzing = isAnalyzing;
  
  // Add a function to handle position preservation during analysis
  const preserveCurrentPosition = (fen) => {
    // Dispatch an event to notify that we're starting analysis and need to preserve position
    window.dispatchEvent(new CustomEvent('stockfish-analysis-starting', {
      detail: {
        fen,
        preservePosition: true,
        timestamp: Date.now()
      }
    }));
    
    // Store the current position in a global variable for recovery if needed
    window.currentFEN = fen;
    
    // 
  };
  
  // OLD ANALYSIS REQUEST EFFECT - COMMENTED OUT (Now handled by useUnifiedStockfishAnalysis)
  /*
  // Add this to the useEffect where analysis is requested
  useEffect(() => {
    if (!fen) return;
    
    // Preserve the current position before starting analysis
    preserveCurrentPosition(fen);
    
    // Set isAnalyzing if no external data and not already analyzing
    if (!analysisData && !externalIsAnalyzing) {
      setIsAnalyzing(true);
    }
    
    // Request analysis for background calculation (even when showStockfish is false)
    // This ensures the evaluation bar always has real data
    if (!analysisData) {
      // Use the useStockfishAnalysis hook if available, otherwise trigger direct event
      if (analyzePosition) {
        analyzePosition(fen);
      } else {
        // Dispatch event for external Stockfish components to handle
        window.dispatchEvent(new CustomEvent('request-stockfish-analysis', {
          detail: {
            fen,
            isAnalysisOnly: true, // Flag to prevent board position reset
            backgroundCalculation: !showStockfish, // Flag to indicate this is background calc
            timestamp: Date.now()
          }
        }));
      }
    }
  }, [fen, analysisData, externalIsAnalyzing, analyzePosition]);
  */
  
  // OLD HANDLE STOCKFISH RESULT - COMMENTED OUT (Now handled by useUnifiedStockfishAnalysis)
  /*
  // Add logic to the handleStockfishResult function to prevent position resets
  const handleStockfishResult = (result) => {
    if (!result) return;
    
    // Update local state with analysis result
    setLocalAnalysis(result);
    setIsAnalyzing(false);
    
    // If this is just an analysis update, ensure it doesn't reset the board position
    if (result.isAnalysisOnly) {
      // Dispatch an event to signal we need to preserve the current position
      window.dispatchEvent(new CustomEvent('stockfish-position-preserved', {
        detail: {
          fen: window.currentFEN || fen,
          timestamp: Date.now()
        }
      }));
    }
  };
  */
  
  // OLD ANALYSIS MATCHES CURRENT FEN - COMMENTED OUT (Now in useUnifiedStockfishAnalysis)
  /*
  // Helper function to check if analysis data matches the current FEN
  const analysisMatchesCurrentFEN = useCallback((data, currentFEN) => {
    if (!data || !currentFEN) return false;
    
    // Check if the analysis data is for this specific position
    const analysisFEN = data.fen || data.positionFEN;
    if (!analysisFEN) return false;
    
    // Compare the FENs, but ignore the move counters (last 2 fields)
    // This ensures we're comparing the actual board positions
    const currentFENBase = currentFEN.split(' ').slice(0, 4).join(' ');
    const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');
    
    return currentFENBase === analysisFENBase;
  }, []);
  */
  
  // OLD GET STARTING POSITION ANALYSIS - COMMENTED OUT (Now in useUnifiedStockfishAnalysis)
  /*
  // Add a function to get hardcoded analysis for the starting position
  const getStartingPositionAnalysis = useCallback(() => {
    const startingFEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    
    return {
      fen: startingFEN,
      depth: 15,
      lines: [
        {
          move: 'e4',
          score: '+0.30',
          pv: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1b5']
        },
        {
          move: 'd4',
          score: '+0.25',
          pv: ['d2d4', 'd7d5', 'c2c4', 'e7e6', 'b1c3']
        },
        {
          move: 'Nf3',
          score: '+0.20',
          pv: ['g1f3', 'd7d5', 'd2d4', 'g8f6', 'c2c4']
        }
      ],
      isComplete: true,
      isFromCache: false,
      pvs: [
        {
          line: 1,
          evaluation: '+0.30',
          normalizedCP: 0.30,
          bestMoveLAN: 'e2e4',
          bestMoveSAN: 'e4',
          pv: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1b5'],
          pvSAN: ['e4', 'e5', 'Nf3', 'Nc6', 'Bb5'],
          userOrientation: 'white',
          positionTurn: 'white',
          depth: 15
        },
        {
          line: 2,
          evaluation: '+0.25',
          normalizedCP: 0.25,
          bestMoveLAN: 'd2d4',
          bestMoveSAN: 'd4',
          pv: ['d2d4', 'd7d5', 'c2c4', 'e7e6', 'b1c3'],
          pvSAN: ['d4', 'd5', 'c4', 'e6', 'Nc3'],
          userOrientation: 'white',
          positionTurn: 'white',
          depth: 15
        },
        {
          line: 3,
          evaluation: '+0.20',
          normalizedCP: 0.20,
          bestMoveLAN: 'g1f3',
          bestMoveSAN: 'Nf3',
          pv: ['g1f3', 'd7d5', 'd2d4', 'g8f6', 'c2c4'],
          pvSAN: ['Nf3', 'd5', 'd4', 'Nf6', 'c4'],
          userOrientation: 'white',
          positionTurn: 'white',
          depth: 15
        }
      ],
      scores: ['+0.3', '+0.25', '+0.20'],
      positionFEN: startingFEN,
      timestamp: Date.now(),
      isAnalysisOnly: true
    };
  }, []);
  */
  
  // OLD MAIN ANALYSIS EFFECT - COMMENTED OUT (Now handled by useUnifiedStockfishAnalysis)
  /*
  // Main analysis effect for FEN changes
  useEffect(() => {
    
    
    
    if (!fen) {
      
      return;
    }
    
    // CRITICAL: If FEN actually changed, force clear all cached analysis first
    if (lastAnalyzedFen && lastAnalyzedFen !== fen) {
      console.log(`🔄 SAP: FEN changed from ${lastAnalyzedFen.substring(0, 20)}... to ${fen.substring(0, 20)}... - clearing all cached analysis`);
      
      // Clear local analysis immediately
      setLocalAnalysis(null);
      
      // Clear global cache
      window.currentAnalysisCache = null;
      
      // Clear analysis request ref to allow new requests
      analysisRequestRef.current = '';
      
      // Reset analyzing state to show "Calculating..." immediately
      setIsAnalyzing(true);
    }
    
    // GUARD: Only skip recalculation if we have analysis for the EXACT current FEN
    if (analysis && analysis.pvs?.length > 0 && analysis.depth >= 15 && 
        analysis.isComplete !== false && analysisMatchesCurrentFEN(analysis, fen)) {
      console.log(`✅ StockfishAnalysisPanel: Already have complete analysis for position at depth ${analysis.depth}`);
      return;
    }
    
    // Check if this is the starting position and use hardcoded analysis
    const startingFEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    if (fen === startingFEN) {
      
      const hardcodedAnalysis = getStartingPositionAnalysis();
      setLocalAnalysis(hardcodedAnalysis);
      setIsAnalyzing(false);
      setLastAnalyzedFen(fen);
      
      // Also set global cache for consistency
      window.currentAnalysisCache = hardcodedAnalysis;
      return;
    }
    
    // CRITICAL: If we already have analysis for this exact FEN, don't re-analyze
    if (lastAnalyzedFen === fen && (localAnalysis || hookAnalysis)) {
      
      return;
    }
    
    // IMPORTANT: Skip analysis during sequential move playback to prevent multiple triggers
    // Check if we're currently in a sequential move sequence
    if (window.currentSequentialMove && window.currentSequentialMove.isSequentialMove && 
        !window.currentSequentialMove.isLastInSequence) {
    //   
      return;
    }
    
    // 
    
    // Note: Cache clearing is now handled at the beginning of this effect
    
    // PHASE 2.1: Check external analysis data provided as prop (with strict FEN validation)
    if (analysisData && analysisData.lines && analysisData.lines.length > 0) {
      const externalMatches = analysisMatchesCurrentFEN(analysisData, fen);
    //   
      if (externalMatches) {
        // 
        setLocalAnalysis(analysisData);
        setIsAnalyzing(false);
        setLastAnalyzedFen(fen);
        return;
      }
    }
    
    // PHASE 2.2: Check global cache (window.currentAnalysisCache) with strict validation
    if (window.currentAnalysisCache) {
      const globalCacheMatches = analysisMatchesCurrentFEN(window.currentAnalysisCache, fen);
    //   
      if (globalCacheMatches) {
        // 
        setLocalAnalysis(window.currentAnalysisCache);
        setIsAnalyzing(false);
        setLastAnalyzedFen(fen);
        return;
      } else {
        // 
        window.currentAnalysisCache = null;
      }
    }
    
    // PHASE 2.3: Direct moves array cache lookup using enhanced functions
    if (window.getCachedAnalysisForFEN && typeof window.getCachedAnalysisForFEN === 'function') {
      const cachedAnalysisByFEN = window.getCachedAnalysisForFEN(fen);
    //   
      if (cachedAnalysisByFEN) {
        // 
        setLocalAnalysis(cachedAnalysisByFEN);
        setIsAnalyzing(false);
        setLastAnalyzedFen(fen);
        // Update global cache for consistency
        window.currentAnalysisCache = cachedAnalysisByFEN;
        return;
      }
    }
    
    // PHASE 2.4: Check by current move index if available (with validation)
    if (window.checkForCachedAnalysis && typeof window.checkForCachedAnalysis === 'function' && 
        window.getCurrentMoveIndex && typeof window.getCurrentMoveIndex === 'function') {
      const currentMoveIndex = window.getCurrentMoveIndex();
    //   
      
      // Always check for cached analysis, including index -1 (start position)
      const hasCachedAnalysis = window.checkForCachedAnalysis(currentMoveIndex, fen);
    //   
      
      if (hasCachedAnalysis && window.currentAnalysisCache && 
          analysisMatchesCurrentFEN(window.currentAnalysisCache, fen)) {
        const positionType = currentMoveIndex === '-1' ? 'start position' : `move ${currentMoveIndex}`;
        // 
        setLocalAnalysis(window.currentAnalysisCache);
        setIsAnalyzing(false);
        setLastAnalyzedFen(fen);
        return;
      } else if (hasCachedAnalysis && window.currentAnalysisCache) {
        const positionType = currentMoveIndex === '-1' ? 'start position' : `move ${currentMoveIndex}`;
        // 
        window.currentAnalysisCache = null;
      }
    }
    
    // PHASE 2.5: No cached analysis found - need to run Stockfish
    
    
    // Check if we've already requested analysis for this FEN
    if (analysisRequestRef.current === fen) {
      
      return;
    }
    
    // Mark that we're requesting analysis for this FEN
    analysisRequestRef.current = fen;
    
    // Preserve the current position first
    preserveCurrentPosition(fen);
    
    // Don't clear existing data when requesting new analysis
    // Just set analyzing state
    setIsAnalyzing(true);
    
    console.log(`🚀 SAP: Requesting new analysis for FEN: ${fen.substring(0, 20)}...`);
    
    // If Stockfish3 instance is available directly, use it with force=true to ensure fresh analysis
    if (stockfish3Instance && stockfish3Instance.handleAnalysisRequest) {
      console.log(`📡 SAP: Using stockfish3Instance.handleAnalysisRequest with force=true`);
      stockfish3Instance.handleAnalysisRequest(fen, true); // Force recalculation
    } else if (window.updatePositionForAnalysis && typeof window.updatePositionForAnalysis === 'function') {
      // Use 2AddGame.js analysis function which will force recalculation
      console.log(`📡 SAP: Using window.updatePositionForAnalysis`);
      window.updatePositionForAnalysis(fen);
    } else {
      // Fallback to hook method
      console.log(`📡 SAP: Using analyzePosition hook with force=true`);
      analyzePosition(fen, true); // Force recalculation
    }
    
    // Update the last analyzed FEN
    setLastAnalyzedFen(fen);
  }, [fen, analyzePosition, analysisData, analysisMatchesCurrentFEN, showStockfish, getStartingPositionAnalysis]); // Removed lastAnalyzedFen to prevent circular dependency
  */
  
  // Handle move selection from analysis lines
  const handleMoveSelect = useCallback((line) => {
    if (onMoveSelect && line) {
      onMoveSelect(line);
    }
  }, [onMoveSelect]);
  
  // Toggle between detailed and simple view
  const toggleDisplayMode = useCallback(() => {
    setDisplayMode(displayMode === 'detailed' ? 'simple' : 'detailed');
  }, [displayMode]);
  
  // Toggle panel expansion
  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);
  
  // OLD FORCE ANALYSIS - COMMENTED OUT (Now use forceReanalyze from unified hook)
  /*
  // Force new analysis
  const handleForceAnalysis = useCallback(() => {
    if (fen) {
    //   
      
      // If Stockfish3 instance is available directly, use it with force=true
      if (stockfish3Instance && stockfish3Instance.handleAnalysisRequest) {
        stockfish3Instance.handleAnalysisRequest(fen, true);
      } else {
        // Fallback to hook method
        analyzePosition(fen, true);
      }
      
      setLastAnalyzedFen(fen);
    }
  }, [fen, analyzePosition]);
  */
  
  // Tab management functions
  const switchToPVLine = useCallback((direction) => {
    if (!analysis?.pvs?.length) return;
    
    const totalLines = Math.min(analysis.pvs.length, 3); // Max 3 lines
    if (direction === 'prev') {
      setSelectedPVIndex((selectedPVIndex - 1 + totalLines) % totalLines);
    } else {
      setSelectedPVIndex((selectedPVIndex + 1) % totalLines);
    }
  }, [selectedPVIndex, analysis]);

  const formatScoreDisplay = useCallback((normalizedCP) => {
    // Handle mate scores (like "M1", "M-3", etc.)
    if (typeof normalizedCP === 'string' && normalizedCP.startsWith('M')) {
      const mateNumber = normalizedCP.substring(1); // Remove 'M' prefix
      const mateValue = parseInt(mateNumber);
      
      if (!isNaN(mateValue)) {
        if (mateValue > 0) {
          return `Mate in ${mateValue}`;
        } else if (mateValue < 0) {
          return `Mate in ${Math.abs(mateValue)}`;
        }
      }
      return normalizedCP; // Return as-is if can't parse
    }
    
    // Handle numeric scores
    if (normalizedCP === undefined || normalizedCP === null) return '0.00';
    const cp = typeof normalizedCP === 'number' ? normalizedCP : parseFloat(normalizedCP);
    if (!isNaN(cp)) {
      return cp >= 0 ? `+${cp.toFixed(2)}` : cp.toFixed(2);
    }
    return '0.00';
  }, []);

  // Add checkmate detection function
  const isPositionCheckmate = useCallback(() => {
    if (!fen) return false;
    
    try {
      const chess = new Chess(fen);
      return chess.isCheckmate();
    } catch (error) {
      console.warn('Error checking for checkmate:', error);
      return false;
    }
  }, [fen]);

  // Special checkmate display component
  const CheckmateDisplay = () => (
    <div className="checkmate-display">
     
        <div className="checkmate-text">Checkmate</div>
       <div className="checkmate-text">
        <i className="fa-solid fa-chess-king fa-beat-fade"></i>
       </div>
     
    </div>
  );

  const convertPVToSAN = useCallback((pvLAN, startingFEN) => {
    if (!pvLAN || !Array.isArray(pvLAN) || pvLAN.length === 0) return [];
    if (!startingFEN) return [];
    
    try {
      const chess = new Chess(startingFEN);
      const sanMoves = [];
      
      for (let i = 0; i < pvLAN.length; i++) {
        const lanMove = pvLAN[i];
        if (!lanMove || lanMove.length < 4) break;
        
        const moveResult = chess.move(lanMove);
        if (moveResult) {
          sanMoves.push(moveResult.san);
        } else {
          console.warn(`⚠️ Could not convert LAN move ${lanMove} to SAN at position ${i}`);
          break;
        }
      }
      return sanMoves;
    } catch (error) {
      console.warn('⚠️ Error converting PV to SAN, returning empty array:', error);
      return []; // Return empty array instead of LAN moves
    }
  }, []);

  // Handle applying a single move to the board
  const handleMoveApplication = useCallback((lineData) => {
    // 
    // 
    // 
    
    if (!lineData) {
    //   
        return;
    }
    
    if (!onMoveSelect) {
    //   
    //   
        return;
    }
    
    // Create move data object for the board
    const moveData = {
      bestMoveSAN: lineData.bestMoveSAN,
      bestMoveLAN: lineData.bestMoveLAN,
      move: lineData.bestMoveSAN || lineData.bestMoveLAN,
      san: lineData.bestMoveSAN,
      lan: lineData.bestMoveLAN,
      score: lineData.normalizedCP,
      evaluation: lineData.normalizedCP,
      isDirectMove: true, // Flag to indicate this is a direct move application
      lineNumber: lineData.lineNumber || lineData.line || 1
    };
    
    // 
    onMoveSelect(moveData);
  }, [onMoveSelect]);

  // Handle sequential move playback for PV tab moves
  const handleSequentialMovePlayback = useCallback((currentLine, clickedMoveIndex) => {
    // console.log('🎬 StockfishAnalysisPanel: Starting sequential playback', {
    //   clickedIndex: clickedMoveIndex,
    //   totalMoves: clickedMoveIndex + 1,
    //   currentLinePV: currentLine.pv,
    //   pvLength: currentLine.pv?.length,
    //   positionFEN: analysis.positionFEN || fen
    // });

    if (!onMoveSelect || !currentLine.pv || currentLine.pv.length === 0) {
    //   
      return;
    }

    // Set the target move (the final move we're playing to)
    setTargetMoveIndex(clickedMoveIndex);
    setCurrentPlayingMoveIndex(null); // Reset current playing move

    // Get the moves to play (from start up to clicked move, inclusive)
    const movesToPlay = currentLine.pv.slice(0, clickedMoveIndex + 1);
    
    // Convert LAN moves to SAN for better representation
    let tempGame = null;
    try {
      tempGame = new Chess(analysis.positionFEN || fen);
    } catch (error) {
      console.error('🚨 Error creating chess instance for sequential playback:', error);
      return;
    }
    
    // Pre-convert all moves to both LAN and SAN notation
    const convertedMoves = [];
    const conversionGame = new Chess(analysis.positionFEN || fen);
    
    for (let i = 0; i < movesToPlay.length; i++) {
      try {
        const moveResult = conversionGame.move(movesToPlay[i]);
        if (moveResult) {
          convertedMoves.push({
            lan: movesToPlay[i],
            san: moveResult.san,
            from: moveResult.from,
            to: moveResult.to
          });
        } else {
          console.error(`🚨 Failed to convert move ${movesToPlay[i]} at position ${i}`);
          break;
        }
      } catch (error) {
        console.error(`🚨 Error converting move ${movesToPlay[i]}:`, error);
        break;
      }
    }
    
    // 
    
    // Set global flag to indicate we're in sequential playback
    window.currentSequentialMove = { isSequentialMove: true, totalMoves: convertedMoves.length };
    
    // Play each move with a 0.75-second delay
    convertedMoves.forEach((moveData, index) => {
      setTimeout(() => {
        // Update current playing move index for highlighting
        setCurrentPlayingMoveIndex(index);
        
        const isLastMove = index === convertedMoves.length - 1;
        
        // Update global sequential move state
        window.currentSequentialMove = {
          isSequentialMove: true,
          sequenceIndex: index,
          totalMoves: convertedMoves.length,
          isLastInSequence: isLastMove
        };
        
        // Create comprehensive move data for this specific move
        const sequentialMoveData = {
          bestMoveLAN: moveData.lan,
          bestMoveSAN: moveData.san,
          move: moveData.lan, // Board component expects LAN for move application
          san: moveData.san,
          lan: moveData.lan,
          from: moveData.from,
          to: moveData.to,
          isSequentialMove: true,
          sequenceIndex: index,
          totalSequence: convertedMoves.length,
          lineNumber: selectedPVIndex + 1,
          // Add timing information for visual feedback
          playbackDelay: 750,
          isLastInSequence: isLastMove,
          // Only trigger analysis for the last move, not intermediate moves
          skipAnalysis: !isLastMove, // Skip analysis for all moves except the last
          triggerAnalysis: isLastMove
        };
        
        onMoveSelect(sequentialMoveData);
        
        // For the last move in sequence, clean up and trigger analysis
        if (isLastMove) {
          setTimeout(() => {
            // Clear move highlighting
            setCurrentPlayingMoveIndex(null);
            setTargetMoveIndex(null);
            
            // Clear the global sequential move flag
            window.currentSequentialMove = null;
            
            // Don't clear, just mark as analyzing for the new position
            // setIsAnalyzing(true); // COMMENTED OUT - Now handled by unified hook
            
            // Clear global cache only if it doesn't match the new position
            const finalFEN = conversionGame.fen();
            // if (window.currentAnalysisCache && !analysisMatchesCurrentFEN(window.currentAnalysisCache, finalFEN)) {
            //   window.currentAnalysisCache = null;
            // } // COMMENTED OUT - Now handled by unified hook
            
            // Dispatch event to force analysis refresh
            window.dispatchEvent(new CustomEvent('force-analysis-refresh', {
              detail: { 
                reason: 'sequential-playback-complete',
                finalPosition: finalFEN
              }
            }));
          }, 100); // Small delay to ensure the board state is updated
        }
      }, index * 750); // 0.75 seconds = 750ms between each move
    });
  }, [onMoveSelect, analysis?.positionFEN, fen, selectedPVIndex, setCurrentPlayingMoveIndex, setTargetMoveIndex]);
  // OLD DEPENDENCY: setLocalAnalysis, setIsAnalyzing
  
  // Debug information
  // COMMENTED OUT: References old state variables
  /*
  const debugInfo = {
    currentFen: fen || 'No FEN provided to component',
    lastAnalyzedFen: lastAnalyzedFen || 'None',
    analysisAvailable: analysis ? 'Yes' : 'No',
    analysisDepth: analysis?.depth || 0,
    isAnalyzing: isAnalyzing ? 'Yes' : 'No',
    lineCount: analysis?.lines?.length || 0,
    usingExternalData: analysisData ? 'Yes' : 'No',
    usingLocalAnalysis: localAnalysis ? 'Yes' : 'No',
    usingWindowCache: window.currentAnalysisCache ? 'Yes' : 'No',
    rawAnalysisType: rawAnalysis ? 
      (rawAnalysis.lines ? 'has lines' : 
       (rawAnalysis.pvs ? 'has pvs' : 
        (rawAnalysis.analysis ? 'has nested analysis' : 'unknown format'))) : 'none'
  };
  */

  // Tab Component Functions
  const renderTabHeader = () => {
    const colors = ['#ffd700', '#59b047', '#3066be'];
    
    return (
      <div className="stockfish-tab-header">
        <div className="tab-header-left">
          {activeTab > 0 ? (
            <button 
              className="tab-back-btn"
              onClick={() => setActiveTab(0)}
              title="Back to Top 3"
            >
              <i className="fas fa-minimize"></i>
            </button>
          ) : null}
          
          {/* Settings gear icon - always visible in left header */}
         
         {/* {activeTab === 0 && <button 
            className="settings-gear-icon"
            onClick={() => setActiveTab(2)}
            title="Settings"
          >
            <i className="fas fa-gear"></i>
          </button>} */}
          

          {activeTab === 0 && (
            <i className="fa-solid fa-robot tab-robot-icon" style={{ fontSize: '20px', color: '#ffd700', marginBottom: '10px', cursor: 'pointer' }} onClick={() => setActiveTab(2)}></i>
          )}

          {/* Show depth for Best Move tab only */}
          {activeTab === 0 && analysis?.depth && (
            <div className="depth-badge" title={`Analysis depth: ${analysis.depth}`}>
              d={analysis.depth}
            </div>
          )}
          
          {/* Refresh button for Best Move tab only */}
          {/* {activeTab === 0 && (
            <button 
              className="refresh-analysis-btn"
              onClick={() => {
                console.log('🔄 Forcing analysis refresh for current FEN:', fen?.substring(0, 20) + '...');
                if (forceReanalyze) {
                  forceReanalyze();
                } else {
                  console.warn('⚠️ forceReanalyze function not available');
                }
              }}
              title="Stop current analysis and recalculate position"
            >
              <i className="fas fa-refresh"></i>
            </button>
          )} */}
        </div>
        
        <div className="tab-header-right">
          {/* Line navigation controls moved to PV tab content */}
        </div>
      </div>
    );
  };

  const renderTopThreeTab = () => {
    const colors = ['#ffd700', '#59b047', '#3066be']; // Updated 2nd color to match header
    const labels = ['Best Move:', '2nd Best:', '3rd Best:'];
    
    // Handle different states more explicitly
    if (!fen || fen.trim() === '') {
      return (
        <div className="tab-content analyzing-load">
          Waiting for position...
        </div>
      );
    }
    
    // PRIORITY CHECK: If we're analyzing, always show calculating state
    // This prevents stale analysis from showing during recalculation
    if (finalIsAnalyzing) {
      return (
        <div className="tab-content analyzing-load">
          Calculating...&nbsp;&nbsp; <i className="fas fa-chess-knight fa-bounce"></i>
        </div>
      );
    }
    
    if (!analysis?.pvs?.length) {
      return (
        <div className="tab-content analyzing-load">
          Calculating...&nbsp;&nbsp; <i className="fas fa-chess-knight fa-bounce"></i>
          {/* Previously said 'no analysis available' */}
        </div>
      );
    }
    
    const topThree = analysis.pvs.slice(0, 3);
    
    // Helper function to get SAN notation for a move
    const getSANMove = (line) => {
      // If we already have SAN, use it
      if (line.bestMoveSAN) {
        return line.bestMoveSAN;
      }
      
      // If we only have LAN, try to convert it to SAN
      if (line.bestMoveLAN && (analysis.positionFEN || fen)) {
        try {
          const chess = new Chess(analysis.positionFEN || fen);
          const moveResult = chess.move(line.bestMoveLAN);
          return moveResult ? moveResult.san : 'N/A';
        } catch (error) {
          console.warn('⚠️ Could not convert LAN to SAN for best move:', line.bestMoveLAN);
          return 'N/A';
        }
      }
      
      return 'N/A';
    };
    
    // Check if all moves have valid SAN notation (not 'N/A')
    const hasValidMoves = topThree.every(line => {
      const sanMove = getSANMove(line);
      return sanMove && sanMove !== 'N/A';
    });
    
    if (!hasValidMoves) {
      return (
        <div className="tab-content analyzing-load">
          Calculating...&nbsp;&nbsp; <i className="fas fa-chess-knight fa-bounce"></i>
        </div>
      );
    }
    
    return (
      <div className="tab-content top-three-tab">
        <div className="best-moves-cards">
          {topThree.map((line, index) => {
            const sanMove = getSANMove(line);
            
            return (
              <div 
                key={index}
                className="best-move-card"
                style={{ backgroundColor: colors[index] }}
                onMouseEnter={() => {
                  if (engineArrows === 'Hover') {
                    setHoveredMoveIndex(index);
                  }
                }}
                onMouseLeave={() => {
                  if (engineArrows === 'Hover') {
                    setHoveredMoveIndex(null);
                  }
                }}
              >
                <div className="card-header">
                  <span className="card-label">{labels[index]}</span>
                  <span 
                    className="expand-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPVIndex(index);
                      setActiveTab(1);
                    }}
                    title="View full line"
                  >
                    <i className="fas fa-maximize"></i>
                  </span>
                </div>
                <div 
                  className="card-move-area"
                  onClick={() => handleMoveApplication(line)}
                  title={`Play ${sanMove}`}
                >
                  <div className="card-move">
                    {sanMove}
                  </div>
                  <div className="card-score">
                    {formatScoreDisplay(line.normalizedCP)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper function to parse FEN and get starting move context
  const getFENMoveContext = (fenString) => {
    if (!fenString) return { activeColor: 'w', fullMoveNumber: 1 };
    
    const fenParts = fenString.split(' ');
    const activeColor = fenParts[1] || 'w'; // 'w' or 'b'
    const fullMoveNumber = parseInt(fenParts[5]) || 1;
    
    return { activeColor, fullMoveNumber };
  };

  // Helper function to group moves into White-Black pairs with proper numbering
  const groupMovesIntoPairs = (sanMoves, fenString) => {
    const { activeColor, fullMoveNumber } = getFENMoveContext(fenString);
    const movePairs = [];
    let currentMoveNumber = fullMoveNumber;
    let isWhiteToMove = activeColor === 'w';
    
    for (let i = 0; i < sanMoves.length; i++) {
      const move = sanMoves[i];
      
      if (isWhiteToMove) {
        // White's turn - start a new pair
        movePairs.push({
          moveNumber: currentMoveNumber,
          white: { move, index: i },
          black: null
        });
        isWhiteToMove = false;
      } else {
        // Black's turn
        if (movePairs.length > 0 && !movePairs[movePairs.length - 1].black) {
          // Complete the current pair
          movePairs[movePairs.length - 1].black = { move, index: i };
        } else {
          // This shouldn't happen in normal circumstances, but handle it
          movePairs.push({
            moveNumber: currentMoveNumber,
            white: null,
            black: { move, index: i }
          });
        }
        isWhiteToMove = true;
        currentMoveNumber++;
      }
    }
    
    return movePairs;
  };

  // Helper function to get move button highlighting styles
  const getMoveHighlightStyle = (moveIndex) => {
    const colors = ['#ffd700', '#59b047', '#3066be']; // Gold, Green, Blue
    const baseColor = colors[selectedPVIndex];
    
    const isCurrentMove = currentPlayingMoveIndex === moveIndex;
    const isTargetMove = targetMoveIndex === moveIndex;
    
    if (isCurrentMove || isTargetMove) {
      return {
        backgroundColor: baseColor,
        color: '#000000', // Black text
        borderColor: baseColor,
        fontWeight: 'bold'
      };
    }
    
    return {
      borderColor: baseColor,
      cursor: 'pointer'
    };
  };

  const renderPVTab = () => {
    // Handle different states more explicitly
    if (!fen || fen.trim() === '') {
      return (
        <div className="tab-content">
          Waiting for position...
        </div>
      );
    }
    
    // PRIORITY CHECK: If we're analyzing, always show calculating state
    // This prevents stale analysis from showing during recalculation
    if (finalIsAnalyzing) {
      return (
        <div className="tab-content analyzing-load">
          Calculating...&nbsp;&nbsp; <i className="fa-solid fa-chess-knight fa-bounce"></i>
        </div>
      );
    }
    
    if (!analysis?.pvs?.length) {
      return <div className="tab-content">No PV data available</div>;
    }
    
    if (selectedPVIndex >= analysis.pvs.length) {
      return <div className="tab-content">No PV data available</div>;
    }
    
    const colors = ['#ffd700', '#59b047', '#3066be']; // Updated 2nd color to match header
    const currentLine = analysis.pvs[selectedPVIndex];
    const sanMoves = convertPVToSAN(currentLine.pv || [], analysis.positionFEN || fen);
    
    // Only render if we have successfully converted moves to SAN
    if (sanMoves.length === 0) {
      return (
        <div className="tab-content analyzing-load">
          Calculating...&nbsp;&nbsp; <i className="fa-solid fa-chess-knight fa-bounce"></i>
        </div>
      );
    }
    
    // Group moves into White-Black pairs with proper numbering
    const movePairs = groupMovesIntoPairs(sanMoves, analysis.positionFEN || fen);
    
    return (
      <div 
        className="tab-content pv-tab"
        style={{ borderColor: colors[selectedPVIndex],
          marginRight: '10px'
         }}
      >
        <div className="pv-header">
          <div className="pv-title-with-controls">
            <i
              onClick={() => switchToPVLine('prev')} 
              disabled={selectedPVIndex === 0} 
              title="Previous line"
              className="fa-solid fa-square-minus" 
              style={{ fontSize: '20px', color: 'gray', cursor: 'pointer' }}
            ></i>

            <h3 style={{ color: colors[selectedPVIndex] }}>
              {selectedPVIndex === 0 ? 'Best Line' : selectedPVIndex === 1 ? '2nd Best Line' : '3rd Best Line'}
            </h3>

            <i 
              onClick={() => switchToPVLine('next')} 
              disabled={selectedPVIndex >= Math.min(analysis.pvs.length - 1, 2)} 
              title="Next line"
              className="fa-solid fa-square-plus" 
              style={{ fontSize: '20px', color: 'gray', cursor: 'pointer' }}
            ></i>
          </div>
          
          <div className="pv-right-info">
            <div className="pv-depth">depth={analysis?.depth || '?'}</div>
            <div className="pv-score">{formatScoreDisplay(currentLine.normalizedCP)}</div>
          </div>
        </div>
        
        <div className="pv-moves">
          {movePairs.map((pair, pairIndex) => {
            return (
              <div key={pairIndex} className="move-pair">
                <span className="move-number">
                  {pair.moveNumber}.
                </span>
                
                {pair.white && (
                  <button 
                    className="pv-move-btn white-move"
                    style={getMoveHighlightStyle(pair.white.index)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSequentialMovePlayback(currentLine, pair.white.index);
                    }}
                    title={`Click to play moves 1-${pair.white.index + 1} in sequence (${pair.white.move})`}
                  >
                    {pair.white.move}
                  </button>
                )}
                
                {pair.black && (
                  <button 
                    className="pv-move-btn black-move"
                    style={getMoveHighlightStyle(pair.black.index)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSequentialMovePlayback(currentLine, pair.black.index);
                    }}
                    title={`Click to play moves 1-${pair.black.index + 1} in sequence (${pair.black.move})`}
                  >
                    {pair.black.move}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSettingsTab = () => {
    return (
      <div className="tab-content settings-tab">
        <div className="settings-header">
          <h2 className="settings-title " style={{ color: '#ffd700', textAlign: 'center' }}>Stockfish 16 - Local Browser</h2>
        </div>
        
        <div className="settings-controls-panel">
          <div className="setting-row">
            <label className="setting-label">Engine Arrows:</label>
            <select 
              value={engineArrows}
              onChange={(e) => setEngineArrows(e.target.value)}
              className="setting-dropdown"
            >
              <option value="None">None</option>
              <option value="Best">Best</option>
              <option value="All">All</option>
              <option value="Hover">Hover</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  // Safe console logging
//   console.log('StockfishAnalysisPanel debug:', {
//     fen,
//     hasLocalAnalysis: !!localAnalysis,
//     hasAnalysisData: !!analysisData,
//     hasHookAnalysis: !!hookAnalysis,
//     hasWindowCache: !!window.currentAnalysisCache,
//     matchingExternalData: analysisMatchesCurrentFEN(analysisData, fen),
//     matchingWindowCache: analysisMatchesCurrentFEN(window.currentAnalysisCache, fen),
//     isAnalyzing,
//     linesCount: analysis?.lines?.length || 0,
//     rawFormat: rawAnalysis ? Object.keys(rawAnalysis) : []
//   });
  
  // OLD START POSITION ANALYSIS EFFECT - COMMENTED OUT (Now handled by useUnifiedStockfishAnalysis)
  /*
  // Handle start position analysis when FEN becomes available or rootFEN changes
  useEffect(() => {
    // Check if we have a valid FEN and should analyze the start position
    if (fen && fen.trim() !== '' && showStockfish) {
      // Get current move index to see if we're at start position
      const currentMoveIndex = window.getCurrentMoveIndex ? window.getCurrentMoveIndex() : null;
      
      if (currentMoveIndex === '-1') {
        // 
        // 
        
        // Check if we already have start position analysis cached for this FEN
        if (window.checkForCachedAnalysis && typeof window.checkForCachedAnalysis === 'function') {
          const hasStartAnalysis = window.checkForCachedAnalysis('-1', fen);
          
          if (!hasStartAnalysis) {
            // 
            setIsAnalyzing(true);
            
            // Use the most reliable analysis method available
            if (window.updatePositionForAnalysis && typeof window.updatePositionForAnalysis === 'function') {
            //   
              window.updatePositionForAnalysis(fen);
            } else {
            //   
              analyzePosition(fen, false);
            }
          } else {
            // 
            
            // Make sure we have the cached analysis in our local state
            if (window.currentAnalysisCache && analysisMatchesCurrentFEN(window.currentAnalysisCache, fen)) {
              setLocalAnalysis(window.currentAnalysisCache);
            }
            setIsAnalyzing(false);
          }
        } else {
          // If caching functions aren't available, just analyze
        //   
          setIsAnalyzing(true);
          analyzePosition(fen, false);
        }
      }
    } else if (!fen || fen.trim() === '') {
    //   
    }
  }, [fen, showStockfish, analyzePosition]); // Watch for FEN changes and showStockfish
  */

  // OLD ANALYSIS EFFECTS - COMMENTED OUT (Now handled by useUnifiedStockfishAnalysis)
  /*
  // Add effect to watch for analysis updates from external sources
  useEffect(() => {
    const handleAnalysisUpdate = () => {
    //   
      
      // Check if we have new analysis available
      if (window.currentAnalysisCache && window.currentAnalysisCache.lines?.length > 0) {
        // 
        setLocalAnalysis(window.currentAnalysisCache);
        setIsAnalyzing(false);
      }
    };

    // Listen for custom events that signal analysis completion
    window.addEventListener('analysis-complete', handleAnalysisUpdate);
    window.addEventListener('stockfish-analysis-ready', handleAnalysisUpdate);
    
    return () => {
      window.removeEventListener('analysis-complete', handleAnalysisUpdate);
      window.removeEventListener('stockfish-analysis-ready', handleAnalysisUpdate);
    };
  }, []);

  // Add effect to monitor startPosition analysis for index -1
  useEffect(() => {
    if (window.getCurrentMoveIndex && window.getCurrentMoveIndex() === '-1') {
      // Check if we have startPosition analysis available
      if (window.checkForCachedAnalysis && window.checkForCachedAnalysis('-1', fen)) {
        if (window.currentAnalysisCache && !localAnalysis) {
        //   
          setLocalAnalysis(window.currentAnalysisCache);
          setIsAnalyzing(false);
        }
      }
    }
  }, [fen, localAnalysis]);

  // Synchronize local isAnalyzing state with hook analysis results
  useEffect(() => {
    if (hookAnalysis && hookAnalysis.lines && hookAnalysis.lines.length > 0) {
      // GUARD: Only update if we're currently analyzing to prevent unnecessary re-renders
      if (isAnalyzing) {
        setIsAnalyzing(false);
        // Clear the analysis request ref since we got results
        analysisRequestRef.current = '';
      }
    }
  }, [hookAnalysis, isAnalyzing]);

  // Send analysis data to parent component when available
  useEffect(() => {
    if (onAnalysisUpdate && analysis && analysis.pvs && analysis.pvs.length > 0) {
      // Only send update if analysis matches current FEN to prevent stale data
      if (analysisMatchesCurrentFEN(analysis, fen)) {
        console.log('SAP456 sending analysis update to parent:', {
          source: 'sap',
          hasPvs: true,
          pvsLength: analysis.pvs.length,
          currentFEN: fen?.substring(0, 20) + '...',
          analysisFEN: (analysis.positionFEN || analysis.fen)?.substring(0, 20) + '...',
          depth: analysis.depth
        });
        
        onAnalysisUpdate({
          ...analysis,
          source: 'sap',
          timestamp: Date.now()
        });
      } else {
        console.log('SAP456 skipping analysis update - FEN mismatch:', {
          currentFEN: fen?.substring(0, 20) + '...',
          analysisFEN: (analysis.positionFEN || analysis.fen)?.substring(0, 20) + '...'
        });
      }
    }
  }, [analysis, onAnalysisUpdate, fen, analysisMatchesCurrentFEN]);
  
  // Also synchronize with localAnalysis
  useEffect(() => {
    if (localAnalysis && localAnalysis.lines && localAnalysis.lines.length > 0) {
      // GUARD: Only update if we're currently analyzing to prevent unnecessary re-renders
      if (isAnalyzing) {
        setIsAnalyzing(false);
        // Clear the analysis request ref since we got results
        analysisRequestRef.current = '';
      }
    }
  }, [localAnalysis, isAnalyzing]);
  */

  // Effect to handle Engine Arrows setting changes
  useEffect(() => {
    // console.log('[StockfishAnalysisPanel] Engine arrows effect triggered:', {
    //   engineArrows,
    //   hasAnalysis: !!analysis,
    //   pvCount: analysis?.pvs?.length || 0,
    //   hoveredMoveIndex
    // });

    // If arrows are disabled or no analysis available, clear arrows
    if (engineArrows === 'None' || !analysis?.pvs?.length) {
      // console.log('[StockfishAnalysisPanel] Clearing arrows');
      window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
        detail: { arrows: [] }
      }));
      return;
    }

    // Handle Hover mode - only show arrow when hovering
    if (engineArrows === 'Hover') {
      if (hoveredMoveIndex === null) {
        // No hover, clear arrows
        window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
          detail: { arrows: [] }
        }));
        return;
      }
      
      // Show only the hovered move arrow
      const colors = ['#ffd700', '#59b047', '#3066be']; // Gold, Green, Blue
      const hoveredMove = analysis.pvs[hoveredMoveIndex];
      
      if (hoveredMove && hoveredMove.bestMoveLAN && hoveredMove.bestMoveLAN.length >= 4) {
        const from = hoveredMove.bestMoveLAN.substring(0, 2);
        const to = hoveredMove.bestMoveLAN.substring(2, 4);
        
        const arrows = [[from, to, colors[hoveredMoveIndex]]];
        // console.log('[StockfishAnalysisPanel] Dispatching hover arrow:', arrows);
        
        window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
          detail: { arrows }
        }));
      }
      return;
    }

    // Handle Best/All modes
    const arrowCount = engineArrows === 'Best' ? 1 : 3;
    const colors = ['#ffd700', '#59b047', '#3066be']; // Gold, Green, Blue
    
    // Build arrows array from analysis
    const arrows = [];
    const topMoves = analysis.pvs.slice(0, arrowCount);
    
    topMoves.forEach((line, index) => {
      if (line.bestMoveLAN && line.bestMoveLAN.length >= 4) {
        const from = line.bestMoveLAN.substring(0, 2);
        const to = line.bestMoveLAN.substring(2, 4);
        
        // 3Board expects format: [from, to, color]
        arrows.push([from, to, colors[index]]);
      }
    });

    // console.log('[StockfishAnalysisPanel] Dispatching arrows:', arrows);
    
    // Dispatch arrows to 3Board
    window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
      detail: { arrows }
    }));
  }, [engineArrows, analysis, hoveredMoveIndex]);

  // Clear hover state when new analysis data arrives
  const previousAnalysisTimestamp = useRef(null);
  useEffect(() => {
    if (analysis && analysis.timestamp && 
        previousAnalysisTimestamp.current !== analysis.timestamp && 
        hoveredMoveIndex !== null) {
      // console.log('[StockfishAnalysisPanel] New analysis received, clearing stale hover state');
      setHoveredMoveIndex(null);
    }
    if (analysis && analysis.timestamp) {
      previousAnalysisTimestamp.current = analysis.timestamp;
    }
  }, [analysis, hoveredMoveIndex]);

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedArrowsSetting = localStorage.getItem('engineArrows');
    if (savedArrowsSetting) {
      // console.log('[StockfishAnalysisPanel] Loading saved arrows setting:', savedArrowsSetting);
      setEngineArrows(savedArrowsSetting);
    }
  }, []);

  // Save to localStorage when engineArrows changes
  useEffect(() => {
    // console.log('[StockfishAnalysisPanel] Saving arrows setting to localStorage:', engineArrows);
    localStorage.setItem('engineArrows', engineArrows);
  }, [engineArrows]);

  return (
    <div className={`stockfish-analysis-panel ${isPositionCheckmate() ? 'checkmate-mode' : 'tabbed-ui'} ${expanded ? 'expanded' : 'collapsed'}`}>
      {/* Check for checkmate first and display special component */}
      {isPositionCheckmate() ? (
        <CheckmateDisplay />
      ) : (
        <>
          {renderTabHeader()}
          
          <div className={`tab-main-content tab-${activeTab}`}
          
          >
            {activeTab === 0 && renderTopThreeTab()}
            {activeTab === 1 && renderPVTab()}
            {activeTab === 2 && renderSettingsTab()}
          </div>
        </>
      )}
    </div>
  );
};

export default StockfishAnalysisPanel; 